<script setup lang="ts">
import {useTemplateRef} from 'vue';
import {SvgIcon} from '../svg.ts';

const props = defineProps<{
  query: string;
  matchCount: number;
  currentMatchOrdinal: number;
  pendingStepCount: number;
  caseSensitive: boolean;
  regex: boolean;
  invalidPattern: boolean;
  locale: Record<string, any>;
}>();

const emit = defineEmits<{
  queryChange: [query: string];
  next: [];
  prev: [];
  toggleCase: [];
  toggleRegex: [];
  close: [];
}>();

const inputEl = useTemplateRef<HTMLInputElement>('inputEl');

defineExpose({
  focus: () => {
    inputEl.value?.focus();
    inputEl.value?.select();
  },
});

function onInput(event: Event) {
  emit('queryChange', (event.target as HTMLInputElement).value);
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault();
    if (event.shiftKey) {
      emit('prev');
    } else {
      emit('next');
    }
  } else if (event.key === 'Escape') {
    event.preventDefault();
    emit('close');
  }
}

function counterText(): string {
  if (props.invalidPattern) return props.locale.searchInvalidPattern;
  const pendingSuffix = props.pendingStepCount === 0 ? '' :
    ` ${props.locale.searchPendingSteps.replace('%d', String(props.pendingStepCount))}`;
  if (props.matchCount === 0) return props.query ? props.locale.searchNoMatches + pendingSuffix : '';
  const base = props.locale.searchMatchCount
    .replace('%d', String(props.currentMatchOrdinal))
    .replace('%d', String(props.matchCount));
  return base + pendingSuffix;
}
</script>

<template>
  <div class="log-find-bar" :class="{'invalid-pattern': invalidPattern}">
    <input
      ref="inputEl"
      type="text"
      class="log-find-input"
      :value="query"
      :placeholder="locale.searchPlaceholder"
      @input="onInput"
      @keydown="onKeydown"
    >
    <span class="log-find-counter">{{ counterText() }}</span>
    <button
      class="btn interact-fg log-find-toggle log-find-case" :class="{active: caseSensitive}"
      :aria-label="locale.searchCaseSensitive" :data-tooltip-content="locale.searchCaseSensitive"
      @click="emit('toggleCase')"
    >
      Aa
    </button>
    <button
      class="btn interact-fg log-find-toggle log-find-regex" :class="{active: regex}"
      :aria-label="locale.searchRegex" :data-tooltip-content="locale.searchRegex"
      @click="emit('toggleRegex')"
    >
      .*
    </button>
    <button
      class="btn interact-fg log-find-nav log-find-prev" :disabled="matchCount === 0"
      :aria-label="locale.searchPrevious" :data-tooltip-content="locale.searchPrevious"
      @click="emit('prev')"
    >
      <SvgIcon
        name="octicon-chevron-down" :size="14" class="log-find-prev-icon"
      />
    </button>
    <button
      class="btn interact-fg log-find-nav log-find-next" :disabled="matchCount === 0"
      :aria-label="locale.searchNext" :data-tooltip-content="locale.searchNext"
      @click="emit('next')"
    >
      <SvgIcon
        name="octicon-chevron-down" :size="14"
      />
    </button>
    <button
      class="btn interact-fg log-find-close"
      :aria-label="locale.searchClose" :data-tooltip-content="locale.searchClose"
      @click="emit('close')"
    >
      <SvgIcon name="octicon-x" :size="14"/>
    </button>
  </div>
</template>

<style scoped>
.log-find-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  background: var(--color-console-menu-bg);
  border: 1px solid var(--color-console-menu-border);
  border-radius: var(--border-radius);
  box-shadow: 0 2px 8px var(--color-shadow);
}

.log-find-bar.invalid-pattern .log-find-input {
  outline: 1px solid var(--color-error-text);
}

.log-find-input {
  background: transparent;
  border: none;
  color: var(--color-console-fg);
  outline: none;
  width: 160px;
}

.log-find-counter {
  color: var(--color-console-fg-subtle);
  font-size: 12px;
  white-space: nowrap;
  padding: 0 4px;
}

.log-find-toggle.active {
  color: var(--color-primary);
  background: var(--color-primary-light-6);
}

/* no dedicated "chevron up" icon exists in this codebase's svg set -- reuse chevron-down,
   rotated, for "previous" rather than adding a new icon asset for one button. */
.log-find-prev-icon {
  transform: rotate(180deg);
}
</style>
